import { Button, Modal } from '@braskai/ui-kit';

import { alpha, css, styled } from '@mui/material';

import { touchHitArea } from '@/shared/lib/touchHitArea';

type CardProps = {
  $isMobile: boolean;
};

type BadgeProps = {
  $tone: 'brand' | 'warning';
};

type BrowserLineProps = {
  $tone: 100 | 200;
  $width: string;
};

type StripProps = {
  $tallLine: boolean;
};

type LanguageChipProps = {
  $active: boolean;
};

type LanguageCycleItemProps = {
  $index: number;
};

export const SDialog = styled(Modal)(
  ({ theme }) => css`
    .MuiBackdrop-root {
      background-color: ${alpha(theme.palette.grey[900], 0.45)};
      backdrop-filter: blur(3px);
    }

    .MuiDialog-paper {
      border-radius: 20px;
      background-color: transparent;
      box-shadow:
        0 24px 64px -12px ${alpha(theme.palette.grey[900], 0.35)},
        0 0 0 1px ${alpha(theme.palette.grey[900], 0.04)};

      ${theme.breakpoints.up('tablet')} {
        max-width: 520px;
      }
    }
  `,
);

export const SCard = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<CardProps>(
  ({ $isMobile, theme }) => css`
    position: relative;
    width: 100%;
    max-width: ${$isMobile ? '100%' : '520px'};
    padding: ${$isMobile ? '32px 20px 24px' : '40px'};
    border-radius: ${$isMobile ? '20px 20px 0 0' : '20px'};
    background-color: ${theme.palette.common.white};
    color: ${theme.palette.grey[900]};
    animation: recording-paywall-fade-up 0.22s ease;

    @keyframes recording-paywall-fade-up {
      from {
        transform: translateY(10px);
        opacity: 0;
      }
      to {
        transform: none;
        opacity: 1;
      }
    }
  `,
);

export const SCloseButton = styled('button')(
  ({ theme }) => css`
    position: absolute;
    top: 22px;
    right: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: ${theme.palette.grey[400]};
    background: transparent;
    font-family: ${theme.typography.fontFamily};
    cursor: pointer;

    &:hover {
      color: ${theme.palette.grey[700]};
      background-color: ${theme.palette.grey[100]};
    }

    ${touchHitArea()};
  `,
);

export const SBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== '$tone',
})<BadgeProps>(
  ({ $tone, theme }) => css`
    display: inline-flex;
    align-items: center;
    height: 26px;
    margin-bottom: 18px;
    padding: 0 12px;
    border-radius: 999px;
    color: ${$tone === 'warning' ? theme.palette.warning[600] : '#5a43d6'};
    background-color: #edeafb;
    font-weight: 600;
    font-size: 11px;
    line-height: 1;
    letter-spacing: 0.06em;
  `,
);

export const STitle = styled('h3')(
  ({ theme }) => css`
    margin: 0 0 12px;
    color: ${theme.palette.grey[900]};
    font-weight: 700;
    font-size: 30px;
    line-height: 36px;
    letter-spacing: -0.02em;
  `,
);

export const SDescription = styled('p')(
  ({ theme }) => css`
    margin: 0 0 20px;
    color: ${theme.palette.grey[500]};
    font-weight: 400;
    font-size: 15px;
    line-height: 22px;
    text-wrap: pretty;
  `,
);

export const SPreview = styled('div')(
  ({ theme }) => css`
    position: relative;
    height: 186px;
    margin-bottom: 24px;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 12px;
    background-color: ${theme.palette.grey[50]};
    overflow: hidden;
  `,
);

export const SBullets = styled('div')(
  () => css`
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 20px;
  `,
);

export const SBullet = styled('div')(
  () => css`
    display: flex;
    align-items: flex-start;
    gap: 12px;
  `,
);

export const SBulletIcon = styled('svg')(
  ({ theme }) => css`
    flex-shrink: 0;
    margin-top: 1px;
    color: ${theme.palette.primary[600]};
  `,
);

export const SBulletText = styled('span')(({ theme }) => ({
  ...theme.typography['body-2'],
  color: theme.palette.grey[900],
  fontWeight: 400,
}));

export const SStrip = styled('div', {
  shouldForwardProp: (prop) => prop !== '$tallLine',
})<StripProps>(
  ({ $tallLine, theme }) => css`
    margin-bottom: 20px;
    padding: ${$tallLine ? '15px 16px 14px' : '15px 16px 13px'};
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 10px;
    background-color: ${theme.palette.grey[50]};
    color: ${theme.palette.grey[700]};
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
  `,
);

export const SCtaButton = styled(Button)(
  ({ theme }) => css`
    height: 52px;
    border-radius: 12px;
    box-shadow: none;
    font-weight: 600;
    font-size: 15px;
    line-height: 20px;

    &:hover {
      border-color: ${theme.palette.primary[700]};
      background-color: ${theme.palette.primary[700]};
    }
  `,
);

const imagePx = (value: number) => `${((value * 100) / 1296).toFixed(3)}cqw`;

export const SDownloadPreview = styled('div')(
  () => css`
    position: relative;
    margin-bottom: 24px;
    container-type: inline-size;
  `,
);

export const SDownloadImage = styled('img')(
  () => css`
    display: block;
    width: 100%;
    height: auto;
    border-radius: 12px;
    user-select: none;
  `,
);

export const SDownloadImagePill = styled('div')(
  ({ theme }) => css`
    position: absolute;
    top: ${imagePx(454)};
    left: ${imagePx(33)};
    display: flex;
    align-items: center;
    gap: ${imagePx(32)};
    min-width: ${imagePx(458)};
    height: ${imagePx(124)};
    padding: ${imagePx(3)} ${imagePx(40)} 0 ${imagePx(44)};
    border-radius: ${imagePx(24)};
    color: ${theme.palette.grey[700]};
    background-color: ${theme.palette.common.white};
    font-weight: 500;
    font-size: ${imagePx(41.2)};
    line-height: 1;
    white-space: nowrap;
  `,
);

export const SDownloadImagePillIcon = styled('svg')(
  () => css`
    flex-shrink: 0;
    width: ${imagePx(49)};
    height: ${imagePx(49)};
  `,
);

export const SLocalizationPreview = styled('div')(
  ({ theme }) => css`
    position: relative;
    height: 186px;
    margin-bottom: 24px;
    border: 1px solid ${theme.palette.primary[100]};
    border-radius: 12px;
    background-color: ${theme.palette.primary[50]};
    overflow: hidden;
  `,
);

export const SLocalizationWindow = styled('div')(
  ({ theme }) => css`
    position: absolute;
    top: 22px;
    left: 50%;
    width: 300px;
    max-width: calc(100% - 32px);
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 12px;
    background-color: ${theme.palette.common.white};
    box-shadow: 0 14px 34px -18px ${alpha(theme.palette.grey[900], 0.45)};
    overflow: hidden;
    transform: translateX(-50%);
  `,
);

export const SLocalizationBody = styled('div')(
  () => css`
    display: flex;
    flex-direction: column;
    gap: 9px;
    padding: 14px 16px;
  `,
);

export const SLocalizationRow = styled('div')(
  () => css`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
  `,
);

export const SCaptionChip = styled('span')(
  ({ theme }) => css`
    display: inline-block;
    max-width: 150px;
    padding: 4px 10px;
    border-radius: 6px;
    color: ${theme.palette.grey[600]};
    background-color: ${theme.palette.grey[100]};
    font-weight: 500;
    font-size: 10px;
    line-height: 14px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  `,
);

export const SLanguagePill = styled('span')(
  ({ theme }) => css`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 10px;
    border-radius: 999px;
    background-color: ${theme.palette.primary[600]};
  `,
);

export const SLanguageCycle = styled('span')(
  () => css`
    position: relative;
    display: inline-block;
    width: 18px;
    height: 12px;
  `,
);

export const SLanguageCycleItem = styled('span', {
  shouldForwardProp: (prop) => prop !== '$index',
})<LanguageCycleItemProps>(
  ({ $index, theme }) => css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.palette.common.white};
    font-weight: 600;
    font-size: 11px;
    line-height: 1;
    opacity: 0;
    animation: recording-paywall-lang-cycle 9.6s ease-in-out infinite;
    animation-delay: ${$index * 2.4}s;

    @keyframes recording-paywall-lang-cycle {
      0% {
        opacity: 0;
      }
      5% {
        opacity: 1;
      }
      22% {
        opacity: 1;
      }
      27% {
        opacity: 0;
      }
      100% {
        opacity: 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      opacity: ${$index === 0 ? 1 : 0};
      animation: none;
    }
  `,
);

export const SLanguageChips = styled('div')(
  () => css`
    position: absolute;
    right: 0;
    bottom: 16px;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
  `,
);

export const SLanguageChip = styled('span', {
  shouldForwardProp: (prop) => prop !== '$active',
})<LanguageChipProps>(
  ({ $active, theme }) => css`
    display: inline-flex;
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    height: 26px;
    padding: 0 11px;
    border: ${$active ? 0 : `1px solid ${theme.palette.grey[200]}`};
    border-radius: 999px;
    color: ${$active ? theme.palette.common.white : theme.palette.grey[600]};
    background-color: ${$active ? theme.palette.primary[600] : theme.palette.common.white};
    font-weight: 600;
    font-size: 12px;
    line-height: 1;
  `,
);

export const SLanguageMore = styled('span')(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 26px;
    padding: 0 10px;
    border-radius: 999px;
    color: ${theme.palette.grey[400]};
    font-weight: 500;
    font-size: 12px;
    line-height: 1;
  `,
);

export const SBrowserMock = styled('div')(
  ({ theme }) => css`
    position: absolute;
    inset: 18px;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 10px;
    background-color: ${theme.palette.common.white};
    box-shadow: 0 8px 22px -10px ${alpha(theme.palette.grey[900], 0.35)};
    overflow: hidden;

    @media (prefers-reduced-motion: reduce) {
      [data-anim] {
        animation: none !important;
      }
    }
  `,
);

export const SZoomLayer = styled('div')(
  () => css`
    position: absolute;
    inset: 0;
    transform-origin: 36% 66%;
    animation: recording-paywall-az-zoom 5.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;

    @keyframes recording-paywall-az-zoom {
      0%,
      12% {
        transform: scale(1);
      }
      34% {
        transform: scale(1.42);
      }
      68% {
        transform: scale(1.42);
      }
      90%,
      100% {
        transform: scale(1);
      }
    }
  `,
);

export const SBrowserBar = styled('div')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 5px;
    height: 22px;
    padding: 0 11px;
    border-bottom: 1px solid ${theme.palette.grey[100]};
    background-color: ${theme.palette.grey[50]};
  `,
);

export const SBrowserDot = styled('span')(
  ({ theme }) => css`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: ${theme.palette.grey[300]};
  `,
);

export const SBrowserBody = styled('div')(
  () => css`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
  `,
);

export const SBrowserLine = styled('span', {
  shouldForwardProp: (prop) => !['$tone', '$width'].includes(prop as string),
})<BrowserLineProps>(
  ({ $tone, $width, theme }) => css`
    width: ${$width};
    height: 8px;
    border-radius: 999px;
    background-color: ${theme.palette.grey[$tone]};
  `,
);

export const SClickTarget = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 66%;
    left: 36%;
    display: inline-flex;
    align-items: center;
    height: 24px;
    padding: 0 12px;
    border-radius: 8px;
    color: ${theme.palette.common.white};
    background-color: ${theme.palette.primary[600]};
    font-weight: 600;
    font-size: 10px;
    line-height: 1;
    white-space: nowrap;
    transform: translate(-50%, -50%);
  `,
);

export const SRipple = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 66%;
    left: 36%;
    width: 38px;
    height: 38px;
    border: 2px solid ${theme.palette.primary[500]};
    border-radius: 50%;
    animation: recording-paywall-az-ripple 5.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;

    @keyframes recording-paywall-az-ripple {
      0%,
      8% {
        transform: translate(-50%, -50%) scale(0.35);
        opacity: 0;
      }
      13% {
        opacity: 0.65;
      }
      34% {
        transform: translate(-50%, -50%) scale(1.9);
        opacity: 0;
      }
      100% {
        opacity: 0;
      }
    }
  `,
);

export const SCursor = styled('svg')(
  ({ theme }) => css`
    position: absolute;
    top: calc(66% + 4px);
    left: calc(36% + 6px);
    color: ${theme.palette.grey[900]};
    transform-origin: top left;

    path {
      fill: currentColor;
      stroke: ${theme.palette.common.white};
    }

    animation: recording-paywall-az-cursor 5.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;

    @keyframes recording-paywall-az-cursor {
      0%,
      9% {
        transform: scale(1);
      }
      13% {
        transform: scale(0.8);
      }
      22% {
        transform: scale(1);
      }
      100% {
        transform: scale(1);
      }
    }
  `,
);

export const SAutoZoomBadge = styled('span')(
  ({ theme }) => css`
    position: absolute;
    bottom: 12px;
    left: 14px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 24px;
    padding: 0 10px;
    border-radius: 999px;
    color: ${theme.palette.grey[800]};
    background-color: ${theme.palette.common.white};
    box-shadow: 0 4px 12px -4px ${alpha(theme.palette.grey[900], 0.28)};
    font-weight: 600;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
  `,
);

export const SAutoZoomBadgeIcon = styled('svg')(
  ({ theme }) => css`
    color: ${theme.palette.primary[600]};
  `,
);

export const SRecBadge = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 12px;
    left: 14px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 24px;
    padding: 0 10px;
    border-radius: 999px;
    color: ${theme.palette.grey[800]};
    background-color: ${theme.palette.common.white};
    box-shadow: 0 4px 12px -4px ${alpha(theme.palette.grey[900], 0.28)};
    font-weight: 600;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
  `,
);

export const SRecDot = styled('span')(
  ({ theme }) => css`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: ${theme.palette.error[500]};
    animation: recording-paywall-rec-blink 1.4s ease-in-out infinite;

    @keyframes recording-paywall-rec-blink {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.35;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
);
